import type { SubscriptionAction } from '@/features/subscriptions';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import { CBadge, CTooltip } from '@coreui/react';
import React from 'react';
import type { SubscriptionPlan } from '../../types';
import { getSubscriptionBadgeColor } from '../../utils/badgeColors';
import { formatDate } from '../../utils/formatting';
import {
  getActionAvailability,
  getSubscriptionStatusDisplayValue,
  getSubscriptionStatusTooltip,
} from '../../utils/subscriptionUtils';
import { ActionLink } from '../ActionLink/index.tsx';

interface SubscriptionStatusProps {
  subscription: SubscriptionPlan;
  shopifyCustomerId: number;
  isActionLoading: (customerId: number) => boolean;
  onAction: (action: SubscriptionAction) => void;
}

export const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  subscription,
  shopifyCustomerId,
  isActionLoading,
  onAction,
}) => {
  const { hasPermission } = usePermissions();
  const actions = getActionAvailability(subscription);
  const currentLoading = isActionLoading(shopifyCustomerId);

  // Permission checks
  const canReactivate = hasPermission(USER_PERMISSIONS.CustomersSubscriptionPlanStatusReactivate);
  const canPauseOrCancel = hasPermission(
    USER_PERMISSIONS.CustomersSubscriptionPlanStatusPauseOrCancel,
  );
  const canShipNow = hasPermission(USER_PERMISSIONS.CustomersNextOrderShipNow);
  const canChangeDate = hasPermission(USER_PERMISSIONS.CustomersNextOrderChangeDispatchDate);

  const renderStatusActions = () => {
    if (
      (!canReactivate && !canPauseOrCancel) ||
      (!actions.canReactivate && !actions.canPause && !actions.canCancel)
    ) {
      return null;
    }

    const statusActions: React.ReactElement[] = [];

    if (canReactivate) {
      statusActions.push(
        <ActionLink
          key="reactivate"
          label="Re-activate"
          available={actions.canReactivate}
          onClick={() => onAction('reactivate')}
          loading={currentLoading}
        />,
      );
    }

    if (canPauseOrCancel) {
      statusActions.push(
        <ActionLink
          key="pause"
          label="Pause"
          available={actions.canPause}
          onClick={() => onAction('pause')}
          loading={currentLoading}
        />,
      );
      statusActions.push(
        <ActionLink
          key="cancel"
          label="Cancel"
          available={actions.canCancel}
          onClick={() => onAction('cancel')}
          loading={currentLoading}
        />,
      );
    }

    return (
      <span className="ms-3">
        {statusActions.map((action, index) => (
          <React.Fragment key={action.key}>
            {index > 0 && ' | '}
            {action}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const renderOrderActions = () => {
    if ((!canShipNow && !canChangeDate) || (!actions.canShipNow && !actions.canChangeDate)) {
      return null;
    }

    const orderActions: React.ReactElement[] = [];

    if (canShipNow) {
      orderActions.push(
        <ActionLink
          key="shipNow"
          label="Ship Now"
          available={actions.canShipNow}
          onClick={() => onAction('shipNow')}
          loading={currentLoading}
        />,
      );
    }

    if (canChangeDate) {
      orderActions.push(
        <ActionLink
          key="changeDate"
          label="Change Dispatch Date"
          available={actions.canChangeDate}
          onClick={() => onAction('changeDispatchDate')}
          loading={currentLoading}
        />,
      );
    }

    return (
      <span className="ms-3">
        {orderActions.map((action, index) => (
          <React.Fragment key={action.key}>
            {index > 0 && ' | '}
            {action}
          </React.Fragment>
        ))}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-3">
        <h3>Subscription Status</h3>
      </div>
      <div className="mb-3">
        <strong>Subscription Plan Status:</strong>{' '}
        <CTooltip content={getSubscriptionStatusTooltip(subscription.status)}>
          <CBadge color={getSubscriptionBadgeColor(subscription.status)}>
            {getSubscriptionStatusDisplayValue(subscription.status)}
          </CBadge>
        </CTooltip>
        {renderStatusActions()}
      </div>
      <div className="mb-3">
        <strong>Subscription managed by:</strong>{' '}
        {subscription.isMigrated ? 'Purrscribe' : 'Ordergroove'}
      </div>
      <div className="mb-3">
        <strong>Next Order Date:</strong> {formatDate(subscription.nextDispatchDate || '')}{' '}
        <span className="text-muted">
          {subscription.isOrderInProgress
            ? '(Order currently in progress)'
            : '(Order is not due yet)'}
        </span>
        {renderOrderActions()}
      </div>
    </div>
  );
};
